<template>
  <FwbTimeline class="docs-titles">
    <FwbTimelineItem>
      <FwbTimelinePoint>
        <span class="font-bold">{{ project ? "✓" : "1" }}</span>
      </FwbTimelinePoint>
      <FwbTimelineContent>
        <FwbTimelineTitle>
          <div :class="project ? 'line-through' : ''">Create a project</div>
        </FwbTimelineTitle>
        <FwbTimelineBody>
          <div v-if="project" class="mt-2">
            Project: <SelectableCode class="mx-1">{{ project.name }}</SelectableCode>
          </div>
          <div v-else class="flex flex-col">
            Give your application name (and optionally the env name) and we'll generate an update URL for you.
            <div class="mt-4">
              <BlueButton :href="company ? `/dashboard/${company.slug}/projects/new` : '/dashboard'">
                Create a project
              </BlueButton>
            </div>
          </div>
        </FwbTimelineBody>
      </FwbTimelineContent>
    </FwbTimelineItem>
    <FwbTimelineItem>
      <FwbTimelinePoint>
        <span class="font-bold">{{ apiKeys.length > 0 || generatedKey ? "✓" : "2" }}</span>
      </FwbTimelinePoint>
      <FwbTimelineContent>
        <FwbTimelineTitle>
          <div :class="apiKeys.length > 0 || generatedKey ? 'line-through' : ''">Generate an API key 🔐</div>
        </FwbTimelineTitle>
        <FwbTimelineBody>
          <div v-if="generatedKey" class="mt-4">
            Generated API key: <SelectableCode class="mx-1">{{ generatedKey }}</SelectableCode> (save it somewhere safe)
          </div>
          <div v-else class="flex flex-col">
            Generate a unique key that allows you to securely deploy your apps, and save it somewhere safe. You can
            manage your keys from the "Settings" page.
            <div v-if="company" class="mt-4 flex items-center">
              <BlueButton @click="generateApiKey()">Generate an API key</BlueButton>
            </div>
          </div>
        </FwbTimelineBody>
      </FwbTimelineContent>
    </FwbTimelineItem>
    <FwbTimelineItem>
      <FwbTimelinePoint>
        <span class="font-bold">{{ Object.keys(project?.metadata || {}).length > 0 ? "✓" : "3" }}</span>
      </FwbTimelinePoint>
      <FwbTimelineContent>
        <FwbTimelineTitle>
          <div :class="Object.keys(project?.metadata || {}).length > 0 ? 'line-through' : ''">
            Configure your application
          </div>
        </FwbTimelineTitle>
        <FwbTimelineBody>
          <NuxtLink v-if="route.path === '/docs'" to="#how-to-configure">Follow the instructions</NuxtLink>
          <NuxtLink v-else @click="showHowToModal">Follow the instructions</NuxtLink>
          then run
          <SelectableCode class="mx-1">
            OTAGO_API_KEY={{ generatedKey || "[api_key]" }} npx otago doctor --project {{ projectName }}
          </SelectableCode>
          to check your configuration.

          <FwbModal
            v-if="route.path !== '/docs'"
            class="modal"
            size="5xl"
            :class="isHowToModalDisplayed ? '' : 'hidden'"
            @close="closeHowToModal"
          >
            <template #body>
              <div class="flex mt-[-3.5rem] w-full pb-6 lg:px-4">
                <div>
                  <DocsHowToConfigure :project from-dashboard />
                  <div class="flex items-center">
                    <BlueButton @click="closeHowToModal">Back to dashboard</BlueButton>
                    <ExternalLink href="/docs#how-to-configure" class="ml-5">See complete documentation</ExternalLink>
                  </div>
                </div>
              </div>
            </template>
          </FwbModal>
        </FwbTimelineBody>
      </FwbTimelineContent>
    </FwbTimelineItem>
    <FwbTimelineItem>
      <FwbTimelinePoint><span class="font-bold">4</span></FwbTimelinePoint>
      <FwbTimelineContent>
        <FwbTimelineTitle>Build & Publish</FwbTimelineTitle>
        <FwbTimelineBody>
          Use your usual way of <strong>building</strong> your app (with all the necessary environment variables) and
          <strong>distribute</strong> it (either share a development version or publish it on app stores).
        </FwbTimelineBody>
      </FwbTimelineContent>
    </FwbTimelineItem>
    <FwbTimelineItem>
      <FwbTimelinePoint><span class="font-bold">5</span></FwbTimelinePoint>
      <FwbTimelineContent>
        <FwbTimelineTitle>Run your first code push 🚀</FwbTimelineTitle>
        <FwbTimelineBody>
          Run
          <SelectableCode class="mx-1">
            OTAGO_API_KEY={{ generatedKey || "[api_key]" }} npx otago deploy --project {{ projectName }}
          </SelectableCode>
        </FwbTimelineBody>
      </FwbTimelineContent>
    </FwbTimelineItem>
  </FwbTimeline>
</template>

<script setup lang="ts">
import {
  FwbModal,
  FwbTimeline,
  FwbTimelineBody,
  FwbTimelineContent,
  FwbTimelineItem,
  FwbTimelinePoint,
  FwbTimelineTitle,
} from "flowbite-vue";

const route = useRoute();
const { project_ref } = route.params;

const company = useCompany();
const projects = useCompanyProjects();
const apiKeys = useCompanyApiKeys();
const project = computed(
  () => projects.value.find((p) => p.ref === project_ref) || projects.value[projects.value.length - 1],
);
const projectName = computed(() => project.value?.slug || "[project_ref]");

refreshCompanyApiKeys();

const generatedKey = ref(import.meta.browser ? JSON.parse(sessionStorage.getItem("api_keys") || "[]")[0]?.key : null);

const isHowToModalDisplayed = ref(false);
const showHowToModal = () => (isHowToModalDisplayed.value = true);
const closeHowToModal = () => (isHowToModalDisplayed.value = false);

async function generateApiKey() {
  if (!company.value) return;

  const { data: apiKeyData, error } = await api_post_company_api_key(company.value.id, {
    name: "",
    scopes: ["Deploy"],
    expires_at: null,
  });

  if (error.value) {
    setFlashMessage({ type: "danger", message: error.value.data.message as string, durationMs: 4000 });
  } else if (apiKeyData.value) {
    setFlashMessage({ type: "success", message: "API created created", durationMs: 4000 });
    const { id, key } = apiKeyData.value;

    generatedKey.value = key;
    sessionStorage.setItem(
      "api_keys",
      JSON.stringify([{ id, key }, ...JSON.parse(sessionStorage.getItem("api_keys") || "[]")]),
    );
  }
}
</script>

<style scoped>
ol {
  @apply ml-4;

  h3 {
    @apply py-1;
  }
}

a {
  @apply text-blue-500 underline cursor-pointer;
}
</style>
